import { sortBy } from 'lodash'
import { createSelector } from 'reselect'

import { outOfOrderSearch } from '../helpers/outOfOrderSearch'
import { isSeries } from '../helpers/books'
import { sortEachCategory } from './sortEachCategory'
import { noEntityHasLegacyAttributeBound } from './noEntitiyHasValueBound'
import { placeCustomAttributesSelector } from './customAttributesFirstOrder'

// Other selector dependencies
import {
  allPlacesSelector,
  placesByCategorySelector,
  placesSortedAtoZSelector,
  stringifiedPlacesByIdSelector,
} from './placesFirstOrder'
import {
  currentTimelineSelector,
  currentViewSelector,
  isPlacesManuallySortedSelector,
  placeFilterIsEmptySelector,
  placeFilterSelector,
  placeSortSelector,
  placesSearchTermSelector,
} from './secondOrder'
import { allNotesInBookSelector } from './secondOrder'
import { allCardsSelector } from './cardsFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Place} Place
 */

/**
 * @type {function(PlottrState): Record<String, Array<Place>>}
 */
export const visibleSortedPlacesByCategorySelector = createSelector(
  allPlacesSelector,
  placesByCategorySelector,
  placeFilterSelector,
  placeFilterIsEmptySelector,
  placeSortSelector,
  isPlacesManuallySortedSelector,
  (allPlaces, placesByCategory, filter, filterIsEmpty, sort, isManuallySorted) => {
    if (!allPlaces.length) return {}

    let visible = placesByCategory
    if (!filterIsEmpty && filter !== null) {
      visible = {}
      allPlaces.forEach((pl) => {
        const matches = Object.keys(filter).some((attr) => {
          if (attr === 'tag') {
            /**
             * @type {Array<number>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number>}
             */
            const placeValues = attr === 'tag' ? pl.tags : []
            return filterValues.some((fId) => {
              return placeValues.some((pId) => {
                return fId === pId
              })
            })
          } else if (attr === 'book') {
            /**
             * @type {Array<number | 'series'>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number | 'series'>}
             */
            const placeValues = pl.bookIds
            return filterValues.some((fId) => {
              return placeValues.some((pId) => {
                return fId === pId
              })
            })
          } else if (attr === 'category') {
            /**
             * @type {Array<number>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number>}
             */
            return filterValues.includes(Number(pl.categoryId))
          } else {
            /**
             * @type {Array<String>}
             */
            const values = filter[attr]
            return values.some((val) => {
              return (val === '' && !pl[attr]) || pl[attr] == '' || (pl[attr] && pl[attr] == val)
            })
          }
        })
        if (matches) {
          if (visible[`${pl.categoryId}`] && visible[`${pl.categoryId}`].length) {
            visible[`${pl.categoryId}`].push(pl)
          } else {
            visible[`${pl.categoryId}`] = [pl]
          }
        }
      })
    }

    return sortEachCategory(visible, sort, isManuallySorted, null, null)
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<Place>>}
 */
export const visibleSortedSearchedPlacesByCategorySelector = createSelector(
  visibleSortedPlacesByCategorySelector,
  placesSearchTermSelector,
  stringifiedPlacesByIdSelector,
  (placeCategories, searchTerm, stringifiedPlaces) => {
    if (!searchTerm) return placeCategories

    const lowSearchTerms = searchTerm
      .toLowerCase()
      .split(' ')
      .filter((x) => x)
    return Object.entries(placeCategories).reduce((acc, nextCategory) => {
      const [key, places] = nextCategory
      const newPlaces = places.filter(({ id }) => {
        return outOfOrderSearch(lowSearchTerms, stringifiedPlaces[id])
      })
      if (newPlaces.length > 0) {
        return {
          ...acc,
          [key]: newPlaces,
        }
      } else {
        return acc
      }
    }, {})
  }
)

/**
 * @type {function(PlottrState): Array<Place>}
 */
export const placesSortedInBookSelector = createSelector(
  placesSortedAtoZSelector,
  currentTimelineSelector,
  (places, bookId) =>
    places.filter((place) => {
      if (place.bookIds.length === 0) return true
      if (place.bookIds.some(isSeries)) return true
      return place.bookIds.includes(bookId)
    })
)

/**
 * @type {function(PlottrState): Array<Place>}
 */
export const visibleSortedPlacesSelector = createSelector(
  allPlacesSelector,
  placeFilterSelector,
  placeFilterIsEmptySelector,
  placeSortSelector,
  (allPlaces, filter, filterIsEmpty, sort) => {
    if (!allPlaces.length) return []

    let visible = allPlaces
    if (!filterIsEmpty && filter !== null) {
      visible = []
      visible = allPlaces.filter((pl) => {
        return Object.keys(filter).some((attr) => {
          if (attr == 'tag') {
            /**
             * @type {Array<number>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number>}
             */
            const placeValues = attr === 'tag' ? pl.tags : []
            return filterValues.some((fId) => {
              return placeValues.some((pId) => {
                return fId === pId
              })
            })
          } else if (attr == 'book') {
            /**
             * @type {Array<number | 'series'>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number | 'series'>}
             */
            const placeValues = pl.bookIds
            return filterValues.some((fId) => {
              return placeValues.some((pId) => {
                return fId === pId
              })
            })
          } else {
            /**
             * @type {Array<String>}
             */
            const values = filter[attr]
            return values.some((val) => {
              return (val === '' && (!pl[attr] || pl[attr] == '')) || (pl[attr] && pl[attr] == val)
            })
          }
        })
      })
    }

    let sortOperands = sort.split('~')
    let attrName = sortOperands[0]
    let direction = sortOperands[1]
    let sortOperand = attrName === 'name' ? [attrName, 'id'] : [attrName, 'name']
    let sorted = sortBy(visible, sortOperand)
    if (direction == 'desc') sorted.reverse()
    return sorted
  }
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const placeCustomAttributesThatCanChangeSelector = createSelector(
  allPlacesSelector,
  placeCustomAttributesSelector,
  noEntityHasLegacyAttributeBound
)

/**
 * @type {function(PlottrState): Array<Place>}
 */
export const placesFilterItemsSelector = createSelector(
  currentViewSelector,
  placesSortedAtoZSelector,
  allNotesInBookSelector,
  allCardsSelector,
  (currentView, places, notes, cards) => {
    switch (currentView) {
      case 'notes': {
        const filteredItems = places.filter((place) =>
          notes.find((note) => note.places?.includes(place.id))
        )
        return filteredItems
      }
      case 'timeline': {
        const filteredItems = places.filter((place) =>
          cards.find((card) => card.places?.includes(place.id))
        )
        return filteredItems
      }
      default:
        return []
    }
  }
)
